import React, { useContext } from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import Col2Component from '../Components/2-col-section-';
import ProfilePic from '../pics/profilepic.jpg';
import { DataContext } from '../Components/DataContext';
import SEOHead from '../Components/SEOHead';
import '../index.css';
import WavyLine from '../Components/wavyline';
import CareImg from '../pics/Handshake Heart.png'
import MissionImg from '../pics/Goal.png'
import PromiseImg from '../pics/Agreement.png'
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaClock, FaAward, FaUserMd, FaHeart, FaShieldAlt } from 'react-icons/fa';
import PageBreadcrumbs from '../Components/PageBreadcrumbs';

function AboutPage() {
  const contextData = useContext(DataContext);
  const { scrollYProgress } = useScroll();
  
  // Parallax transforms
  const y1 = useTransform(scrollYProgress, [0, 1], [0, -300]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, -150]);
  const y3 = useTransform(scrollYProgress, [0, 1], [0, -200]);
  
  // Smooth spring animations
  const springY1 = useSpring(y1, { stiffness: 100, damping: 30 });
  const springY2 = useSpring(y2, { stiffness: 100, damping: 30 });
  const springY3 = useSpring(y3, { stiffness: 100, damping: 30 });

  // Scroll progress indicator
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  if (!contextData || !contextData.AddressInfo || !contextData.AboutMe || !contextData.AboutPage) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-secondary"></div>
      </div>
    );
  }

  const { AddressPart1, AddressPart2, AddressPart3, AddressPart4, GoogleAPImaps} = contextData.AddressInfo;
  const { AboutMeTitle, AboutMeDesc } = contextData.AboutMe;
  const { hero, values, whyChooseUs, location, callToAction } = contextData.AboutPage;

  // SEO structured data for About page
  const aboutPageStructuredData = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "About Drosh The Pod - Manchester Podiatry Clinic",
    "description": "Learn about our experienced podiatrists and Manchester-based podiatry clinic. Professional foot care services with a patient-centered approach.",
    "url": "https://www.droshpod.com/about",
    "mainEntity": {
      "@type": "Organization",
      "name": "Drosh The Pod",
      "description": "Professional podiatry services in Manchester",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Manchester",
        "addressRegion": "Greater Manchester",
        "addressCountry": "GB"
      },
      "telephone": "07809-267586",
      "email": "info@droshpod.com"
    }
  };

  return (
    <>
      <SEOHead 
        title="About Us - Drosh The Pod | Manchester Podiatry Clinic"
        description="Learn about our experienced podiatrists and Manchester-based podiatry clinic. Professional foot care services with a patient-centered approach. Book your appointment today."
        keywords="podiatry manchester, foot care clinic, podiatrist about, manchester podiatry, foot treatment, professional podiatry services"
        canonical="https://www.droshpod.com/about"
        structuredData={aboutPageStructuredData}
      />
      
      <main className="relative overflow-hidden min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        {/* Scroll Progress Bar */}
        <motion.div
          className="fixed top-0 left-0 right-0 h-1 bg-gradient-to-r from-secondary to-primary origin-left z-50"
          style={{ scaleX }}
        />

        {/* Floating Background Elements */}
        <motion.div 
          className="fixed inset-0 pointer-events-none z-0"
          style={{ y: springY1 }}
        >
          <div className="absolute top-20 left-10 w-20 h-20 bg-primary/10 rounded-full blur-xl"></div>
          <div className="absolute top-40 right-20 w-16 h-16 bg-secondary/10 rounded-full blur-xl"></div>
          <div className="absolute bottom-40 left-1/4 w-24 h-24 bg-primary/5 rounded-full blur-xl"></div>
        </motion.div>

        {/* Parallax Background Elements */}
        <motion.div 
          className="absolute inset-0 pointer-events-none"
          style={{ y: springY2 }}
        >
          <div className="absolute top-1/4 right-10 w-32 h-32 border border-primary/20 rounded-full"></div>
          <div className="absolute bottom-1/3 left-10 w-24 h-24 border border-secondary/20 rounded-full"></div>
        </motion.div>

        {/* Wavy Background with Parallax */}
        <motion.div 
          className="absolute inset-0 top-1/2 overflow-hidden -z-10"
          style={{ y: springY3 }}
        >
          <WavyLine />
        </motion.div>

        {/* Breadcrumbs */}
        <div className="container mx-auto px-4 pt-6 relative z-10">
          <PageBreadcrumbs />
        </div>
        
        {/* Hero Section */}
        <section className="relative z-10">
          <div className="flex flex-col mt-10 min-h-[20vh]">
            <div className="w-full p-6 flex items-center justify-center">
              <motion.div
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 1.5, ease: "easeOut" }}
                className="text-center lg:text-left max-w-4xl">
                <h1 className="text-4xl lg:text-6xl font-bold text-black tracking-tight mb-4">{hero.title}</h1>
                <p className="text-gray-600 text-xl lg:text-2xl mb-4">{hero.subtitle}</p>
                <p className="text-gray-500 text-lg">{hero.description}</p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* About Section */}
        <section className="py-16 relative z-10">
          <Col2Component
            title={AboutMeTitle}
            description={AboutMeDesc}
            imageProfile={ProfilePic}
            signatureName="Droshna Denderowicz"
          />
        </section>

        {/* Values Section */}
        <section className="relative py-20 px-4 z-10">
          <div className="absolute left-0 w-full h-full transform -skew-y-6 bg-white/60 backdrop-blur-sm -z-10"></div>
          <div className="container mx-auto">
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-800 mb-4">{values.title}</h2>
              <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                {values.subtitle}
              </p>
            </motion.div>
            
            <div className="flex flex-col space-y-8 max-w-4xl mx-auto">
              <motion.div
                initial={{ x: -100, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                transition={{ duration: 1.2, ease: "easeOut" }}
                viewport={{ once: true, margin: "-100px" }}
                className="flex flex-col md:flex-row items-center gap-6 transform hover:scale-105 transition-all duration-500 ease-in-out"
              >
                <div className="bg-white p-8 rounded-2xl shadow-xl w-full md:max-w-md">
                  <div className="flex items-center mb-4">
                    <FaHeart className="text-red-500 text-2xl mr-3" />
                    <h3 className="text-2xl lg:text-3xl font-bold">{values.care.title}</h3>
                  </div>
                  <p className="text-gray-600 text-lg">{values.care.description}</p>
                </div>
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-lg">
                  <img className='w-10' src={CareImg} alt="Patient Care Icon" />  
                </div>
              </motion.div>
              
              <motion.div
                initial={{ x: 100, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                transition={{ duration: 1.2, ease: "easeOut", delay: 0.2 }}
                viewport={{ once: true, margin: "-100px" }}
                className="flex flex-col md:flex-row items-center gap-6 md:ml-12 transform hover:scale-105 transition-all duration-500 ease-in-out"
              >
                <div className="bg-white p-8 rounded-2xl shadow-xl w-full md:max-w-md">
                  <div className="flex items-center mb-4">
                    <FaUserMd className="text-secondary text-2xl mr-3" />
                    <h3 className="text-2xl lg:text-3xl font-bold">{values.mission.title}</h3>
                  </div>
                  <p className="text-gray-600 text-lg">{values.mission.description}</p>
                </div>
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-lg">
                  <img className='w-10' src={MissionImg} alt="Mission Icon" />  
                </div>
              </motion.div>
              
              <motion.div
                initial={{ x: -100, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                transition={{ duration: 1.2, ease: "easeOut", delay: 0.4 }}
                viewport={{ once: true, margin: "-100px" }}
                className="flex flex-col md:flex-row items-center gap-6 md:ml-24 transform hover:scale-105 transition-all duration-500 ease-in-out"
              >
                <div className="bg-white p-8 rounded-2xl shadow-xl w-full md:max-w-md">
                  <div className="flex items-center mb-4">
                    <FaShieldAlt className="text-green-500 text-2xl mr-3" />
                    <h3 className="text-2xl lg:text-3xl font-bold">{values.promise.title}</h3>
                  </div>
                  <p className="text-gray-600 text-lg">{values.promise.description}</p>
                </div>
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-lg">
                  <img className='w-10' src={PromiseImg} alt="Promise Icon" />  
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Why Choose Us Section */}
        <section className="py-16 bg-white/50 backdrop-blur-sm relative z-10">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-800 mb-4">{whyChooseUs.title}</h2>
              <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                {whyChooseUs.subtitle}
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
              {whyChooseUs.benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ y: 50, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.6, delay: 0.1 * (index + 1) }}
                  viewport={{ once: true }}
                  className="text-center p-6 rounded-lg hover:shadow-lg transition-shadow duration-300"
                >
                  {index === 0 && <FaAward className="text-4xl text-secondary mx-auto mb-4" />}
                  {index === 1 && <FaHeart className="text-4xl text-red-500 mx-auto mb-4" />}
                  {index === 2 && <FaShieldAlt className="text-4xl text-green-500 mx-auto mb-4" />}
                  {index === 3 && <FaClock className="text-4xl text-purple-500 mx-auto mb-4" />}
                  <h3 className="text-xl font-semibold mb-2">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Location & Contact Section */}
        <section className='container mx-auto px-4 py-16 relative z-10'>
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className='space-y-8'
          >
            <div className="text-center mb-8">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-800 mb-4">{location.title}</h2>
              <p className="text-gray-600 text-lg">{location.subtitle}</p>
            </div>
            
            <div className='w-full aspect-video'>
              <iframe
                src={GoogleAPImaps}
                className='w-full h-full rounded-3xl shadow-xl'
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Drosh The Pod Manchester Clinic Location"
              />
            </div>
            
            <div className='grid grid-cols-1 lg:grid-cols-2 gap-8'>
              <div className='space-y-4'>
                <h3 className='text-2xl lg:text-3xl font-bold text-gray-800'>{location.addressTitle}</h3>
                <div className='space-y-2 text-lg'>
                  <div className="flex items-center">
                    <FaMapMarkerAlt className="text-secondary mr-3 text-xl" />
                    <div>
                      <p className="font-medium">{AddressPart1}</p>
                      <p>{AddressPart2}</p>
                      <p>{AddressPart3}</p>
                      <p>{AddressPart4}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className='space-y-4'>
                <h3 className='text-2xl lg:text-3xl font-bold text-gray-800'>{location.contactTitle}</h3>
                <div className='space-y-3 text-lg'>
                  <div className="flex items-center">
                    <FaPhone className="text-green-500 mr-3 text-xl" />
                    <a href="tel:07809267586" className="hover:text-secondary transition-colors">
                      07809 267586
                    </a>
                  </div>
                  <div className="flex items-center">
                    <FaEnvelope className="text-secondary mr-3 text-xl" />
                    <a href="mailto:info@droshpod.com" className="hover:text-secondary transition-colors">
                      info@droshpod.com
                    </a>
                  </div>
                  <div className="flex items-center">
                    <FaClock className="text-purple-500 mr-3 text-xl" />
                    <div>
                      <p className="font-medium">{location.openingHours.title}</p>
                      <p>{location.openingHours.weekdays}</p>
                      <p>{location.openingHours.saturday}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </section>

        {/* Call to Action Section */}
        <section className="bg-gradient-to-r from-secondary to-primary text-white py-16 relative z-10">
          <div className="container mx-auto px-4 text-center">
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="max-w-3xl mx-auto"
            >
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">{callToAction.title}</h2>
              <p className="text-xl mb-8 opacity-90">
                {callToAction.description}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a 
                  href={callToAction.buttons.primary.link} 
                  className="bg-white text-blue-600 px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors duration-300"
                >
                  {callToAction.buttons.primary.text}
                </a>
                <a 
                  href={callToAction.buttons.secondary.link} 
                  className="border-2 border-white text-white px-8 py-3 rounded-full font-semibold hover:bg-white hover:text-blue-600 transition-colors duration-300"
                >
                  {callToAction.buttons.secondary.text}
                </a>
              </div>
            </motion.div>
          </div>
        </section>
      </main>
    </>
  );
}

export default AboutPage;